/**
 * Endless mode: puzzles generated by construction, so every one is solvable.
 *
 *  1. Hide masses on the plate.
 *  2. Aim beacons so their beams pass near the hidden masses, and trace them.
 *  3. Put telescopes on the traced paths (multi-beam telescopes where a fan re-converges).
 *  4. Add rocks and walls on the straight, unbent paths only, never on the bent ones.
 *  5. Remove the masses and give them to the player. The hidden layout is the reference solution.
 *
 * Layouts are rejected when they are boring or fragile: the empty board must not win, every
 * telescope must be far from where the unbent beams go, and the hidden solution must survive
 * small nudges.
 */
import { MASS_SPECS, RECEIVER_CATCH, SOURCE_RADIUS, WORLD_H, WORLD_W } from './constants';
import { isRobust, pathDistance } from './analysis';
import { canPlace, traceLevel, weightOf, wins } from './level';
import type { BeamTrace } from './physics';
import { type Rng, mixSeed, mulberry32 } from './rng';
import type { Disc, Level, Mass, MassKind, Nebula, Receiver, Source, Wall } from './types';

export const MAX_TIER = 6;

interface TierSpec {
  masses: MassKind[][];
  sources: [number, number];
  colours: boolean;
  fanChance: number;
  obstacles: [number, number];
  fixedChance: number;
  nebulaChance: number;
  decoyChance: number;
}

const TIERS: Record<number, TierSpec> = {
  1: {
    masses: [['moon'], ['star']],
    sources: [1, 1],
    colours: false,
    fanChance: 0,
    obstacles: [0, 0],
    fixedChance: 0,
    nebulaChance: 0,
    decoyChance: 0,
  },
  2: {
    masses: [['star'], ['giant'], ['moon']],
    sources: [1, 2],
    colours: false,
    fanChance: 0,
    obstacles: [1, 2],
    fixedChance: 0,
    nebulaChance: 0,
    decoyChance: 0.3,
  },
  3: {
    masses: [
      ['moon', 'star'],
      ['moon', 'moon'],
      ['star', 'star'],
    ],
    sources: [2, 2],
    colours: true,
    fanChance: 0.25,
    obstacles: [1, 2],
    fixedChance: 0,
    nebulaChance: 0,
    decoyChance: 0.5,
  },
  4: {
    masses: [
      ['moon', 'star'],
      ['star', 'giant'],
      ['moon', 'void'],
    ],
    sources: [2, 3],
    colours: true,
    fanChance: 0.35,
    obstacles: [1, 3],
    fixedChance: 0.4,
    nebulaChance: 0.3,
    decoyChance: 0.5,
  },
  5: {
    masses: [
      ['moon', 'star', 'moon'],
      ['star', 'void'],
      ['giant', 'moon'],
    ],
    sources: [2, 3],
    colours: true,
    fanChance: 0.4,
    obstacles: [2, 3],
    fixedChance: 0.6,
    nebulaChance: 0.4,
    decoyChance: 0.5,
  },
  6: {
    masses: [
      ['moon', 'star', 'giant'],
      ['moon', 'moon', 'void'],
      ['star', 'star', 'moon'],
    ],
    sources: [2, 3],
    colours: true,
    fanChance: 0.45,
    obstacles: [2, 4],
    fixedChance: 0.7,
    nebulaChance: 0.5,
    decoyChance: 0.6,
  },
};

const ADJ = [
  'Quiet',
  'Pale',
  'Silver',
  'Hollow',
  'Far',
  'Slow',
  'Bright',
  'Cold',
  'Distant',
  'Gentle',
  'Lonely',
  'Wandering',
  'Old',
  'Faint',
  'Hidden',
  'Twin',
];
const NOUN = [
  'Meridian',
  'Transit',
  'Drift',
  'Perihelion',
  'Arc',
  'Halo',
  'Caustic',
  'Zenith',
  'Nadir',
  'Parallax',
  'Ecliptic',
  'Orbit',
  'Crescent',
  'Corona',
  'Horizon',
  'Lantern',
];

const r0 = (v: number) => Math.round(v);
const inside = (x: number, y: number, m: number) =>
  x > m && x < WORLD_W - m && y > m && y < WORLD_H - m;

/** Point on a beam at arc length s. */
function pointAt(b: BeamTrace, s: number): { x: number; y: number; color: number } | null {
  let acc = 0;
  for (const run of b.runs) {
    const p = run.pts;
    for (let i = 0; i + 3 < p.length; i += 2) {
      const d = Math.hypot(p[i + 2]! - p[i]!, p[i + 3]! - p[i + 1]!);
      if (acc + d >= s) {
        const t = d > 0 ? (s - acc) / d : 0;
        return {
          x: p[i]! + t * (p[i + 2]! - p[i]!),
          y: p[i + 1]! + t * (p[i + 3]! - p[i + 1]!),
          color: run.color,
        };
      }
      acc += d;
    }
  }
  return null;
}

/** Where two beams come closest to each other, after a given arc length on the first. */
function meeting(
  a: BeamTrace,
  b: BeamTrace,
  after: number,
): { x: number; y: number; d: number } | null {
  let best: { x: number; y: number; d: number } | null = null;
  for (let s = after; s < a.length; s += 6) {
    const p = pointAt(a, s);
    if (!p || !inside(p.x, p.y, 70)) continue;
    const d = pathDistance(b, p.x, p.y);
    if (!best || d < best.d) best = { x: p.x, y: p.y, d };
  }
  return best;
}

function attempt(rng: Rng, tier: number): Level | null {
  const spec = TIERS[tier]!;
  const kinds = rng.pick(spec.masses);
  const level: Level = {
    id: 'endless',
    name: `${rng.pick(ADJ)} ${rng.pick(NOUN)}`,
    sources: [],
    receivers: [],
    walls: [],
    rocks: [],
    dust: [],
    nebulae: [],
    fixed: [],
    inventory: {},
    par: 0,
    solution: [],
  };

  // 1. hidden masses
  const hidden: Mass[] = [];
  for (const kind of kinds) {
    let placed = false;
    for (let t = 0; t < 60 && !placed; t++) {
      const x = r0(rng.range(220, 1000));
      const y = r0(rng.range(130, 670));
      if (hidden.every((m) => Math.hypot(m.x - x, m.y - y) > 190)) {
        hidden.push({ kind, x, y });
        placed = true;
      }
    }
    if (!placed) return null;
  }
  // fixed scenery masses
  if (rng.chance(spec.fixedChance)) {
    const kind: MassKind =
      tier >= 5 && rng.chance(0.5) ? 'hole' : rng.pick(['star', 'giant'] as MassKind[]);
    const x = r0(rng.range(300, 900));
    const y = r0(rng.range(160, 640));
    if (hidden.some((m) => Math.hypot(m.x - x, m.y - y) < 210)) return null;
    level.fixed.push({ kind, x, y });
  }

  // 2. beacons aimed past the hidden masses
  const nSources = rng.int(spec.sources[0], spec.sources[1]);
  const palette = spec.colours ? [1, 2, 4, 7] : [7];
  for (let i = 0; i < nSources; i++) {
    const target = hidden[i % hidden.length]!;
    const side = rng.int(0, 3); // mostly from the left
    const sx = side < 3 ? r0(rng.range(60, 150)) : r0(rng.range(250, 950));
    const sy = side < 3 ? r0(rng.range(80, 720)) : rng.chance(0.5) ? 60 : WORLD_H - 60;
    const spec2 = MASS_SPECS[target.kind];
    const b =
      (rng.chance(0.5) ? 1 : -1) *
      rng.range(spec2.radius * 2.1, spec2.radius * 2.1 + Math.abs(spec2.mu) * 6 + 40);
    const dx = target.x - sx;
    const dy = target.y - sy;
    const L = Math.hypot(dx, dy);
    const ax = target.x + (-dy / L) * b;
    const ay = target.y + (dx / L) * b;
    const angle = Math.round(((Math.atan2(ay - sy, ax - sx) * 180) / Math.PI) * 2) / 2;
    const src: Source = { x: sx, y: sy, angle, color: rng.pick(palette) };
    if (rng.chance(spec.fanChance)) {
      src.beams = 2;
      src.spread = rng.int(8, 16);
    }
    if (level.sources.some((s) => Math.hypot(s.x - sx, s.y - sy) < 90)) return null;
    level.sources.push(src);
  }
  // a nebula somewhere along a bent path
  const traceNow = () => traceLevel(level, hidden);
  let t = traceNow();
  if (rng.chance(spec.nebulaChance) && t.beams.length > 0) {
    const b = rng.pick(t.beams);
    const p = pointAt(b, b.length * rng.range(0.35, 0.6));
    if (p && b.color === 7) {
      const neb: Nebula = {
        x: r0(p.x + rng.range(-30, 30)),
        y: r0(p.y + rng.range(-30, 30)),
        r: r0(rng.range(60, 90)),
        color: rng.pick([1, 2, 4, 3, 5, 6]),
      };
      level.nebulae.push(neb);
      t = traceNow();
    }
  }
  if (t.beams.some((b) => b.reason !== 'exit' || b.length < 500)) return null;

  // 3. telescopes on the traced paths
  const receivers: Receiver[] = [];
  const clearOf = (x: number, y: number, gap: number) =>
    inside(x, y, 75) &&
    level.sources.every((s) => Math.hypot(s.x - x, s.y - y) > gap + SOURCE_RADIUS + 40) &&
    receivers.every((r) => Math.hypot(r.x - x, r.y - y) > gap + 60) &&
    [...hidden, ...level.fixed].every(
      (m) => Math.hypot(m.x - x, m.y - y) > gap + MASS_SPECS[m.kind].radius + 30,
    );
  let k = 0;
  while (k < t.beams.length) {
    const b = t.beams[k]!;
    const src = level.sources[b.source]!;
    const fan = (src.beams ?? 1) > 1;
    const partner = t.beams[k + 1];
    if (
      fan &&
      partner &&
      partner.source === b.source &&
      level.nebulae.length === 0 &&
      rng.chance(0.6)
    ) {
      const m = meeting(b, partner, 260);
      if (m && m.d < 6 && clearOf(m.x, m.y, 30)) {
        receivers.push({ x: r0(m.x), y: r0(m.y), color: src.color, count: 2 });
        k += 2;
        continue;
      }
    }
    let placed = false;
    for (let tries = 0; tries < 20 && !placed; tries++) {
      const p = pointAt(b, b.length * rng.range(0.55, 0.92));
      if (p && clearOf(p.x, p.y, 30)) {
        receivers.push({ x: r0(p.x), y: r0(p.y), color: p.color || b.color });
        placed = true;
      }
    }
    if (!placed) return null;
    k++;
  }
  level.receivers = receivers;

  // telescopes must be far from where the unbent beams go
  const empty = traceLevel(level, []);
  for (const r of receivers) {
    for (const b of empty.beams) if (pathDistance(b, r.x, r.y) < RECEIVER_CATCH * 2.3) return null;
  }

  // 4. obstacles on the straight paths only
  const [o0, o1] = spec.obstacles;
  const nObs = rng.int(o0, o1);
  const solved = traceLevel(level, hidden);
  for (let i = 0; i < nObs; i++) {
    const b = rng.pick(empty.beams);
    const p = pointAt(b, b.length * rng.range(0.3, 0.7));
    if (!p) continue;
    const useWall = rng.chance(0.35);
    if (useWall) {
      const a =
        Math.atan2(p.y - level.sources[b.source]!.y, p.x - level.sources[b.source]!.x) +
        Math.PI / 2;
      const half = rng.range(50, 110);
      const w: Wall = {
        x1: r0(p.x - Math.cos(a) * half),
        y1: r0(p.y - Math.sin(a) * half),
        x2: r0(p.x + Math.cos(a) * half),
        y2: r0(p.y + Math.sin(a) * half),
      };
      if (
        ![w.x1, w.x2].every((v) => v > 20 && v < WORLD_W - 20) ||
        ![w.y1, w.y2].every((v) => v > 20 && v < WORLD_H - 20)
      )
        continue;
      level.walls.push(w);
    } else {
      const rock: Disc = { x: r0(p.x), y: r0(p.y), r: r0(rng.range(28, 46)) };
      level.rocks.push(rock);
    }
    const ok =
      solved.beams.every((sb) => {
        if (useWall) {
          const w = level.walls[level.walls.length - 1]!;
          for (let s = 0; s <= 1; s += 0.05) {
            if (pathDistance(sb, w.x1 + (w.x2 - w.x1) * s, w.y1 + (w.y2 - w.y1) * s) < 26)
              return false;
          }
          return true;
        }
        const rk = level.rocks[level.rocks.length - 1]!;
        return pathDistance(sb, rk.x, rk.y) > rk.r + 24;
      }) &&
      hidden.every((m, j) => canPlace(level, hidden, m.kind, m.x, m.y, j)) &&
      receivers.every((r) => {
        const rk = level.rocks[level.rocks.length - 1];
        return useWall || !rk || Math.hypot(r.x - rk.x, r.y - rk.y) > rk.r + 50;
      });
    if (!ok) {
      if (useWall) level.walls.pop();
      else level.rocks.pop();
    }
  }

  // 5. verify (and drop hidden masses the solution does not actually need)
  if (!hidden.every((m, j) => canPlace(level, hidden, m.kind, m.x, m.y, j))) return null;
  for (let j = hidden.length - 1; j >= 0 && hidden.length > 1; j--) {
    const without = hidden.filter((_, q) => q !== j);
    if (wins(level, without)) hidden.splice(j, 1);
  }
  if (!wins(level, hidden) || wins(level, [])) return null;
  if (!isRobust(level, hidden, 2.5)) return null;

  const inventory: Level['inventory'] = {};
  for (const m of hidden) inventory[m.kind] = (inventory[m.kind] ?? 0) + 1;
  if (rng.chance(spec.decoyChance)) {
    const decoy = rng.pick(['moon', 'star', 'giant'] as MassKind[]);
    inventory[decoy] = (inventory[decoy] ?? 0) + 1;
  }
  return { ...level, inventory, par: weightOf(hidden), solution: hidden };
}

/** Generate the endless puzzle for (seed, tier). Pure: the same inputs give the same puzzle. */
export function generateLevel(seed: number, tier: number): Level {
  const tr = Math.max(1, Math.min(MAX_TIER, Math.round(tier)));
  const rng = mulberry32(mixSeed(seed, tr));
  for (let i = 0; i < 600; i++) {
    const lvl = attempt(rng, tr);
    if (lvl) return { ...lvl, id: `endless-${seed}-${tr}` };
  }
  // Fall back to the simplest tier, which practically always succeeds.
  const easy = mulberry32(mixSeed(seed, 99));
  for (let i = 0; i < 2000; i++) {
    const lvl = attempt(easy, 1);
    if (lvl) return { ...lvl, id: `endless-${seed}-1` };
  }
  throw new Error('generator failed');
}

/** Tier for the n-th puzzle of an endless run: gets harder every two solves. */
export function tierForRun(solved: number): number {
  return Math.min(MAX_TIER, 1 + Math.floor(solved / 2));
}
